import { useEffect, useRef } from 'react';
import mapboxgl from 'mapbox-gl';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../services/api.js';
import { useMapStore } from '../../stores/mapStore.js';
import type { Location, LocationSupport } from '../../types/index.js';

interface LocationMarkerLayerProps {
  map: mapboxgl.Map;
}

export function LocationMarkerLayer({ map }: LocationMarkerLayerProps) {
  const { flyTo, activeLocationId, setActiveLocation } = useMapStore();
  const mainMarkersRef = useRef<mapboxgl.Marker[]>([]);
  const supportMarkersRef = useRef<mapboxgl.Marker[]>([]);

  const { data: locations = [] } = useQuery({
    queryKey: ['locations'],
    queryFn: async () => {
      const result = await api.get<{ locations: Location[] }>('/locations');
      return result.success ? result.data.locations : [];
    },
  });

  // Support locations for whichever location is active
  const { data: supports = [] } = useQuery({
    queryKey: ['location-supports', activeLocationId],
    queryFn: async () => {
      const result = await api.get<{ supports: LocationSupport[] }>(`/locations/${activeLocationId}/supports`);
      return result.success ? result.data.supports : [];
    },
    enabled: !!activeLocationId,
  });

  // Main location markers
  useEffect(() => {
    mainMarkersRef.current.forEach(m => m.remove());
    mainMarkersRef.current = [];

    locations.forEach(loc => {
      const el = document.createElement('div');
      el.className = 'cursor-pointer select-none text-xl transition-transform hover:scale-125';
      el.textContent = '📌';
      el.title = loc.name;
      el.style.filter = activeLocationId === loc.id
        ? 'drop-shadow(0 0 8px #7c3aed)'
        : 'drop-shadow(0 0 4px #9333ea)';

      el.addEventListener('click', () => {
        const next = activeLocationId === loc.id ? null : loc.id;
        setActiveLocation(next);
        flyTo(loc.lng, loc.lat);
      });

      const displayName = loc.businessName || loc.name;
      const marker = new mapboxgl.Marker({ element: el })
        .setLngLat([loc.lng, loc.lat])
        .setPopup(
          new mapboxgl.Popup({ offset: 25, closeButton: false }).setHTML(
            `<div class="text-sm font-semibold">${displayName}</div>${loc.businessName ? `<div class="text-xs text-gray-500">${loc.name}</div>` : ''}<div class="text-xs text-gray-400">${loc.address}</div>`,
          ),
        )
        .addTo(map);

      mainMarkersRef.current.push(marker);
    });

    return () => { mainMarkersRef.current.forEach(m => m.remove()); mainMarkersRef.current = []; };
  }, [locations, activeLocationId, map, flyTo, setActiveLocation]);

  // Support location markers (yellow dots)
  useEffect(() => {
    supportMarkersRef.current.forEach(m => m.remove());
    supportMarkersRef.current = [];

    supports.forEach(sup => {
      const el = document.createElement('div');
      el.title = sup.name;
      el.style.cssText = 'width:14px;height:14px;background:#f59e0b;border-radius:50%;border:2px solid white;box-shadow:0 0 8px rgba(245,158,11,0.8);cursor:pointer;';

      el.addEventListener('click', () => flyTo(sup.lng, sup.lat));

      const marker = new mapboxgl.Marker({ element: el })
        .setLngLat([sup.lng, sup.lat])
        .setPopup(
          new mapboxgl.Popup({ offset: 20, closeButton: false }).setHTML(
            `<div class="text-sm font-semibold">${sup.name}</div><div class="text-xs text-gray-500">${sup.address}</div>`,
          ),
        )
        .addTo(map);

      supportMarkersRef.current.push(marker);
    });

    return () => { supportMarkersRef.current.forEach(m => m.remove()); supportMarkersRef.current = []; };
  }, [supports, map, flyTo]);

  return null;
}
